// File: src/lib/graphql/queries/organization.ts
// Purpose: Organization Management GraphQL Queries and Mutations
// VIBE - Organization API

import { gql } from 'graphql-request'
import { executeGraphQL } from '../client'

// ============================================
// TYPES
// ============================================

export interface Organization {
  _id: string
  name: string
  code: string
  legalName?: string
  registrationNumber?: string
  taxId?: string
  website?: string
  email?: string
  phone?: string
  logo?: string
  foundedDate?: string
  employeeCount?: number
  is_active: boolean
  is_deleted: boolean
  createdAt: string
  updatedAt: string
  address: {
    street: string
    city: string
    state: string
    country: string
    postalCode: string
  }
}

export interface OrganizationType {
  id: string
  name: string
  code: string
  description?: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface Department {
  id: string
  name: string
  code: string
  description?: string
  organization?: Organization
  designations?: Designation[]
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface Designation {
  _id: string
  title: string
  code: string
  level?: number
  description?: string
  minExperienceYears?: number
  maxExperienceYears?: number
  skills?: string[]
  employeeCount?: number
  is_active: boolean
  is_deleted?: boolean
  createdAt: string
  updatedAt: string
  department?: {
    _id: string
    name: string
    code: string
    description?: string
    employeeCount?: number
    is_active: boolean
    is_deleted?: boolean
    createdAt: string
    updatedAt: string
  }
}

export interface Role {
  _id: string
  title: string
  is_active: boolean
}

// Helper types for paginated responses
interface OrganizationsResponse {
  nextCursor?: string
  edges: Organization[]
}

interface RolesResponse {
  nextCursor?: string
  edges: Role[]
}

interface DepartmentsResponse {
  nextCursor?: string
  edges: {
    _id: string
    code: string
    name: string
    description?: string
    is_active: boolean
    createdAt: string
    updatedAt: string
  }[]
}

interface DeleteResponse {
  success: boolean
  message: string
}

// ============================================
// QUERIES
// ============================================

export const ORGANIZATIONS_QUERY = gql`
  query Organizations($limit: Int) {
    organizations(limit: $limit) {
      nextCursor
      edges {
        _id
        name
        code
        legalName
        registrationNumber
        taxId
        website
        email
        phone
        logo
        foundedDate
        employeeCount
        is_active
        is_deleted
        createdAt
        updatedAt
        address {
          street
          city
          state
          country
          postalCode
        }
      }
    }
  }
`

export const ORGANIZATION_QUERY = gql`
  query Organization($id: ID!) {
    organization(id: $id) {
      id
      name
      code
      description
      type {
        id
        name
        code
        description
      }
      address
      phone
      email
      website
      departments {
        id
        name
        code
        description
        isActive
      }
      isActive
      createdAt
      updatedAt
    }
  }
`

export const ORGANIZATION_BY_CODE_QUERY = gql`
  query OrganizationByCode($code: String!) {
    organizationByCode(code: $code) {
      id
      name
      code
      description
      type {
        id
        name
        code
      }
      isActive
      createdAt
      updatedAt
    }
  }
`

export const ORGANIZATION_TYPES_QUERY = gql`
  query OrganizationTypes($first: Int, $after: String) {
    organizationTypes(first: $first, after: $after) {
      edges {
        node {
          id
          name
          code
          description
          isActive
          createdAt
          updatedAt
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`

export const DEPARTMENTS_QUERY = gql`
  query Departments($first: Int) {
     departments(limit: $first) {
        nextCursor
        edges {
            _id
            code
            name
            description
            is_active
            createdAt
            updatedAt
        }
    }
  }
`

export const DEPARTMENT_QUERY = gql`
  query Department($id: ID!) {
    department(id: $id) {
      id
      name
      code
      description
      organization {
        id
        name
        code
      }
      designations {
        id
        name
        code
        level
        isActive
      }
      isActive
      createdAt
      updatedAt
    }
  }
`

export const DESIGNATIONS_QUERY = gql`
  query Designations($department: String, $limit: Int) {
    designations(department: $department, limit: $limit) {
      edges {
        _id
        title
        code
        level
        description
        minExperienceYears
        maxExperienceYears
        skills
        employeeCount
        is_active
        is_deleted
        createdAt
        updatedAt
        department {
          _id
          name
          code
          description
          employeeCount
          is_active
          is_deleted
          createdAt
          updatedAt
        }
      }
    }
  }
`

export const ROLES_QUERY = gql`
  query Roles($limit: Int) {
    roles(limit: $limit) {
      nextCursor
      edges {
        _id
        title
        is_active
      }
    }
  }
`

export const DESIGNATIONS_BY_DEPARTMENT_QUERY = gql`
  query DesignationsByDepartment($departmentId: ID!) {
    designationsByDepartment(departmentId: $departmentId) {
      edges {
        node {
          id
          name
          code
          description
          level
          isActive
          createdAt
          updatedAt
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`

// ============================================
// MUTATIONS
// ============================================

export const CREATE_ORGANIZATION_MUTATION = gql`
  mutation CreateOrganization($input: CreateOrganizationInput!) {
    createOrganization(input: $input) {
      _id
      name
      code
      legalName
      registrationNumber
      taxId
      website
      email
      phone
      logo
      foundedDate
      employeeCount
      is_active
      is_deleted
      createdAt
      updatedAt
      organizationType {
        _id
        name
        code
        description
        sector
        subSector
        employeeRangeMin
        employeeRangeMax
        features
        maxDepartments
        maxDesignations
        is_active
        is_deleted
        createdAt
        updatedAt
      }
      address {
        street
        city
        state
        country
        postalCode
      }
    }
  }
`

export const UPDATE_ORGANIZATION_MUTATION = gql`
  mutation UpdateOrganization($input: UpdateOrganizationInput!) {
    updateOrganization(input: $input) {
      _id
      name
      code
      legalName
      registrationNumber
      taxId
      website
      email
      phone
      logo
      foundedDate
      employeeCount
      is_active
      is_deleted
      createdAt
      updatedAt
      organizationType {
        _id
        name
        code
        description
        sector
        subSector
        employeeRangeMin
        employeeRangeMax
        features
        maxDepartments
        maxDesignations
        is_active
        is_deleted
        createdAt
        updatedAt
      }
      address {
        street
        city
        state
        country
        postalCode
      }
    }
  }
`

export const DELETE_ORGANIZATION_MUTATION = gql`
  mutation DeleteOrganization($id: ID!) {
    deleteOrganization(id: $id) {
      _id
      name
      code
    }
  }
`

export const TOGGLE_ORGANIZATION_STATUS_MUTATION = gql`
  mutation ToggleOrganizationStatus($id: ID!) {
    toggleOrganizationStatus(id: $id) {
      id
      isActive
    }
  }
`

export const CREATE_DEPARTMENT_MUTATION = gql`
  mutation CreateDepartment($input: CreateDepartmentInput!) {
    createDepartment(input: $input) {
      _id
      name
      code
      description
      employeeCount
      is_active
      is_deleted
      createdAt
      updatedAt
      headOfDepartment {
        azureId
        azureTenantId
        azureEmail
        azureDisplayName
        ssoEnabled
        lastAzureLogin
        azureGroups
        _id
        firstName
        lastName
        email
        countryCode
        mobile_number
        languages
        joining_date
        skills
        designation
        department
        shift
        is_active
        is_deleted
        createdAt
        updatedAt
        role {
          _id
          title
          is_active
        }
      }
    }
  }
`

export const UPDATE_DEPARTMENT_MUTATION = gql`
  mutation UpdateDepartment($input: UpdateDepartmentInput!) {
    updateDepartment(input: $input) {
      _id
      name
      code
      description
      employeeCount
      is_active
      createdAt
      updatedAt
      headOfDepartment {
        azureId
        azureTenantId
        azureEmail
        azureDisplayName
        ssoEnabled
        lastAzureLogin
        azureGroups
        _id
        firstName
        lastName
        email
        countryCode
        mobile_number
        languages
        joining_date
        skills
        designation
        department
        shift
        is_active
        is_deleted
        createdAt
        updatedAt
        role {
          _id
          title
          is_active
        }
      }
    }
  }
`

export const DELETE_DEPARTMENT_MUTATION = gql`
  mutation DeleteDepartment($id: ID!) {
    deleteDepartment(id: $id) {
      _id
      name
      code
      description
    }
  }
`

export const CREATE_DESIGNATION_MUTATION = gql`
  mutation CreateDesignation($input: CreateDesignationInput!) {
    createDesignation(input: $input) {
      _id
      title
      code
      level
      department {
        _id
        name
        code
        description
        employeeCount
        is_active
        createdAt
        updatedAt
      }
      description
      minExperienceYears
      maxExperienceYears
      skills
      employeeCount
      is_active
      createdAt
      updatedAt
    }
  }
`

export const UPDATE_DESIGNATION_MUTATION = gql`
  mutation UpdateDesignation($input: UpdateDesignationInput!) {
    updateDesignation(input: $input) {
      _id
      title
      code
      level
      description
      minExperienceYears
      maxExperienceYears
      skills
      employeeCount
      is_active
      is_deleted
      createdAt
      updatedAt
      department {
        _id
        name
        code
        description
        employeeCount
        is_active
        createdAt
        updatedAt
      }
    }
  }
`

export const DELETE_DESIGNATION_MUTATION = gql`
  mutation DeleteDesignation($id: ID!) {
    deleteDesignation(id: $id) {
      _id
      title
      code
      level
      description
      minExperienceYears
      maxExperienceYears
      skills
      employeeCount
      is_active
      is_deleted
      createdAt
      updatedAt
    }
  }
`

export const CREATE_ROLE_MUTATION = gql`
  mutation CreateRole($input: CreateRoleInput!) {
    createRole(input: $input) {
      _id
      title
      is_active
    }
  }
`

// ============================================
// API SERVICE CLASS
// ============================================

export class OrganizationAPI {
  // Organization Queries
  static async getOrganizations(limit: number = 10): Promise<OrganizationsResponse> {
    const { organizations } = await executeGraphQL<{ organizations: OrganizationsResponse }>(
      ORGANIZATIONS_QUERY,
      { limit }
    )
    return organizations
  }

  static async getOrganization(id: string): Promise<Organization> {
    const { organization } = await executeGraphQL<{ organization: Organization }>(
      ORGANIZATION_QUERY,
      { id }
    )
    return organization
  }

  static async getOrganizationByCode(code: string): Promise<Organization> {
    const { organizationByCode } = await executeGraphQL<{ organizationByCode: Organization }>(
      ORGANIZATION_BY_CODE_QUERY,
      { code }
    )
    return organizationByCode
  }

  static async getOrganizationTypes(first: number = 10, after?: string): Promise<any> {
    const { organizationTypes } = await executeGraphQL<{ organizationTypes: any }>(
      ORGANIZATION_TYPES_QUERY,
      { first, after }
    )
    return organizationTypes
  }

  // Department Queries
  static async getDepartments(first: number = 10): Promise<DepartmentsResponse> {
    const { departments } = await executeGraphQL<{ departments: DepartmentsResponse }>(
      DEPARTMENTS_QUERY,
      { first }
    )
    return departments
  }

  static async getDepartment(id: string): Promise<Department> {
    const { department } = await executeGraphQL<{ department: Department }>(
      DEPARTMENT_QUERY,
      { id }
    )
    return department
  }

  // Designation Queries
  static async getDesignations(limit: number = 10, department?: string): Promise<{ edges: Designation[] }> {
    const { designations } = await executeGraphQL<{ designations: { edges: Designation[] } }>(
      DESIGNATIONS_QUERY,
      { department, limit }
    )
    return designations
  }

  static async getDesignationsByDepartment(departmentId: string): Promise<any> {
    const { designationsByDepartment } = await executeGraphQL<{ designationsByDepartment: any }>(
      DESIGNATIONS_BY_DEPARTMENT_QUERY,
      { departmentId }
    )
    return designationsByDepartment
  }

  // Role Queries
  static async getRoles(limit: number = 10): Promise<RolesResponse> {
    const { roles } = await executeGraphQL<{ roles: RolesResponse }>(
      ROLES_QUERY,
      // { limit }
    )
    return roles
  }

  // Organization Mutations
  static async createOrganization(input: Partial<Organization>): Promise<Organization> {
    const { createOrganization } = await executeGraphQL<{ createOrganization: Organization }>(
      CREATE_ORGANIZATION_MUTATION,
      { input }
    )
    return createOrganization
  }

  static async updateOrganization(input: Partial<Organization>): Promise<Organization> {
    const { updateOrganization } = await executeGraphQL<{ updateOrganization: Organization }>(
      UPDATE_ORGANIZATION_MUTATION,
      { input }
    )
    return updateOrganization
  }

  static async deleteOrganization(id: string): Promise<Organization> {
    const { deleteOrganization } = await executeGraphQL<{ deleteOrganization: Organization }>(
      DELETE_ORGANIZATION_MUTATION,
      { id }
    )
    return deleteOrganization
  }

  static async toggleOrganizationStatus(id: string): Promise<Organization> {
    const { toggleOrganizationStatus } = await executeGraphQL<{ toggleOrganizationStatus: Organization }>(
      TOGGLE_ORGANIZATION_STATUS_MUTATION,
      { id }
    )
    return toggleOrganizationStatus
  }

  // Department Mutations
  static async createDepartment(input: Partial<Department>): Promise<Department> {
    const { createDepartment } = await executeGraphQL<{ createDepartment: Department }>(
      CREATE_DEPARTMENT_MUTATION,
      { input }
    )
    return createDepartment
  }

  static async updateDepartment(input: Partial<Department>): Promise<Department> {
    const { updateDepartment } = await executeGraphQL<{ updateDepartment: Department }>(
      UPDATE_DEPARTMENT_MUTATION,
      { input }
    )
    return updateDepartment
  }

  static async deleteDepartment(id: string): Promise<Department> {
    const { deleteDepartment } = await executeGraphQL<{ deleteDepartment: Department }>(
      DELETE_DEPARTMENT_MUTATION,
      { id }
    )
    return deleteDepartment
  }

  // Designation Mutations
  static async createDesignation(input: Partial<Designation>): Promise<Designation> {
    const { createDesignation } = await executeGraphQL<{ createDesignation: Designation }>(
      CREATE_DESIGNATION_MUTATION,
      { input }
    )
    return createDesignation
  }

  static async updateDesignation(input: Partial<Designation>): Promise<Designation> {
    const { updateDesignation } = await executeGraphQL<{ updateDesignation: Designation }>(
      UPDATE_DESIGNATION_MUTATION,
      { input }
    )
    return updateDesignation
  }

  static async deleteDesignation(id: string): Promise<Designation> {
    const { deleteDesignation } = await executeGraphQL<{ deleteDesignation: Designation }>(
      DELETE_DESIGNATION_MUTATION,
      { id }
    )
    return deleteDesignation
  }

  // Role Mutations
  static async createRole(input: { title: string; is_active: boolean }): Promise<Role> {
    const { createRole } = await executeGraphQL<{ createRole: Role }>(
      CREATE_ROLE_MUTATION,
      { input }
    )
    return createRole
  }
}